"use client";
import { useState } from "react";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { isAvailableToOrder } from "@/utils/product/isAvailableToOrder.ts";
import { GridGalleryUI } from "./GridGalleryUI";
import {
  hasSocialProofMessages,
  videoPreloadValues,
  SocialProofMessagesProperties,
} from "@/components/Galleries/utils/utils.ts";
import { parseFlagEnumValue } from "@/utils/flags/parseFlagEnumValue.ts";
import {
  standardComponent,
  useStandardProps,
} from "@/context/StandardPropsContext/StandardPropsContext";
import { isM2MRomanBlindOrCurtain } from "@/utils/product/isMadeToMeasure.ts";
import { filterM2MImages } from "@/components/Galleries/utils/filterM2MImages.ts";
import { useRouter } from "next/router";

export const GridGallery = standardComponent("GridGallery", () => {
  const {
    product,
    sizes,
    configProps,
    flags,
    experiencesRawObject,
    runtimeConfig,
  } = useStandardProps();
  const router = useRouter();
  const { variant } = useVariant(product);

  const [showSocialProofMessages, setShowSocialProofMessages] = useState(
    flags.showSocialProofMessages.value === true,
  );
  const [fadeOutSocialProofMessages, setFadeOutSocialProofMessages] =
    useState(false);

  const videoPreload = parseFlagEnumValue(
    String(flags.videoPreload?.value),
    videoPreloadValues,
    "auto",
  );

  const pdpAsLanding = configProps?.pdpAsLanding === true;

  if (!variant) return null;

  const media = "media" in product ? product.media : null;

  const videos = media?.videos || [];

  const title = variant.title;

  let images = variant.images;
  if (isM2MRomanBlindOrCurtain(product)) {
    images = filterM2MImages(images, router.query);
  }

  const spm = hasSocialProofMessages(configProps)
    ? configProps.SocialProofMessages
    : undefined;
  const socialProofMessagesProperties: SocialProofMessagesProperties =
    product.__typename === "SimpleProduct" && spm
      ? {
          appId: spm.appId ?? "",
          withClose: spm.withClose || true,
          productId: product.aliases.pimProductId,
          brand: product.brand.name,
          availableToOrder: isAvailableToOrder(product),
          experiencesRawObject,
          showSocialProofMessages: showSocialProofMessages,
          setShowSocialProofMessages,
          fadeOutSocialProofMessages,
          setFadeOutSocialProofMessages,
          monetateEnvironment: runtimeConfig.monetateEnvironment,
        }
      : undefined;

  return (
    <GridGalleryUI
      title={title}
      images={images}
      videos={videos}
      sizes={sizes}
      hasSocialProofMessagesExperience={true}
      socialProofMessagesProperties={socialProofMessagesProperties}
      hasSuperZoom={!flags.isSuperZoomDisabled.value}
      usePrefetching={!!flags.usePrefetching.value}
      useResponsiveImages={!!flags.useResponsiveImages.value}
      pdpAsLanding={pdpAsLanding}
      videoPreload={videoPreload}
      carouselGalleryInpTest={flags.carouselGalleryInpTest.value === true}
      galleryImageZoomOnHover={flags.galleryImageZoomOnHover.value === true}
      experiencesRawObject={experiencesRawObject}
      runtimeConfig={runtimeConfig}
    />
  );
});
